import {
  Component,
  OnInit,
  Input,
  ElementRef,
  ChangeDetectionStrategy,
  AfterViewInit,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewChecked,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { find, isEqual, slice } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';
import { BulkArrayValue, BulkValue } from '@nimbus-global/features/bulk-edit/models/bulk-value';
import { IIdName } from '@nimbus-library/features/shared/models/interfaces';
import { sortByLocale } from '@nimbus-library/features/localization/utils/active-locale';
import { NimbusIcon } from '../../directives/nimbus-icon.directive';

export interface INimbusTag {
  name: string;
  postFix?: NimbusIcon;
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-tag-list',
  templateUrl: './nimbus-tag-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusTagListComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked {
  @Input() public tagNames: Array<string | INimbusTag> = [];
  @Input() public removableTag: boolean = false;
  @Input() public tagsListChanged: number;
  @Input() public mode: 'single-line' | 'multi-line' = 'single-line';
  @Input() public resizeThrottling = true;
  @Input() public isBulk: boolean = false;
  @Input() public bulkDisabled: boolean = false;
  @Input() public andMorePostfix: string;

  @Input() public bulkValues: BulkArrayValue<IIdName>;

  @Output() public removedTag = new EventEmitter<string | INimbusTag>();

  @ViewChild('tagDisplay') public tagDisplay: ElementRef;

  private readonly ON_CHANGE_DEBOUNCE = 250;
  private initialized = false;

  public icons = ICONS;
  public TranslationKeys = TranslationKeys;

  public expanded = false;
  public triggerRender = false;
  public lastContainerSize = 0;
  public transformedTagNames: Array<string | INimbusTag> = [];
  public displayedTags$: BehaviorSubject<Array<string | INimbusTag>> = new BehaviorSubject([]);

  private get addAndMoreTag(): boolean {
    return this.andMorePostfix && !this.removableTag;
  }

  public get showExpand(): boolean {
    return !this.expanded && this.transformedTagNames?.length !== this.displayedTags$.value?.length;
  }

  public constructor(
    public readonly element: ElementRef,
    private readonly windowEventService: WindowEventsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    let resize$ = this.windowEventService.getResize$();
    if (this.resizeThrottling) {
      resize$ = resize$.pipe(debounceTime(this.ON_CHANGE_DEBOUNCE));
    }

    resize$.pipe(untilDestroyed(this)).subscribe(() => this.onMutated());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.tagNames) {
      this.transformedTagNames = sortByLocale(changes.tagNames.currentValue, (x) => x['name'] || x);
      if (this.addAndMoreTag) {
        this.transformedTagNames.push(this.andMorePostfix);
      }
      this.expanded = false;
      this.triggerRender = true;
      this.updateDisplayedTags([]);
      if (!changes.tagNames.firstChange) {
        this.onMutated();
      }
    }
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.mode === 'single-line' ? this.hideSingleLineOverflow() : this.hideMultiLineOverflow();
    });

    this.initialized = true;
  }

  public ngAfterViewChecked(): void {
    if (
      this.triggerRender &&
      (this.transformedTagNames.length === this.tagDisplay.nativeElement.children.length ||
        this.element.nativeElement.clientWidth !== this.lastContainerSize)
    ) {
      this.triggerRender = false;
      this.onMutated();
    }
  }

  public expand(): void {
    this.updateDisplayedTags([...this.transformedTagNames]);
    this.expanded = true;
  }

  public onMutated(): void {
    this.expanded = false;
    if (this.initialized) {
      this.mode === 'single-line' ? this.hideSingleLineOverflow() : this.hideMultiLineOverflow();
    }
    this.changeDetectorRef.markForCheck();
  }

  public removeTag(tag: string | { name: string; postFix?: string }): void {
    const el = find(this.tagNames, (t) => isEqual(t, tag));
    if (el) {
      this.removedTag.emit(el);
    }
  }

  public getBulkValue(id: string | INimbusTag): BulkValue<IIdName> {
    if (!this.isBulk || !this.bulkValues) {
      return null;
    }
    return this.bulkValues.value.find((bulkItem) => bulkItem.value.name === (id['name'] || id));
  }

  private hideSingleLineOverflow(): void {
    const elContainer = this.element.nativeElement as HTMLDivElement;
    const elTagDisplay = this.tagDisplay.nativeElement as HTMLDivElement;

    const tagMargin = 8;
    const tagEmptyWidth = 28;
    let calculatedWidth = elTagDisplay.clientWidth;
    let containerWidth = elContainer.clientWidth - 1; // -1 is added to cover rounding
    if (calculatedWidth > containerWidth) {
      containerWidth = containerWidth - tagEmptyWidth - tagMargin;
    }

    const tagElements = elTagDisplay.children;
    const maxIndex = tagElements.length - 1;

    let removeCount = 0;
    let isTruncated = false;
    while (this.transformedTagNames && this.transformedTagNames.length !== removeCount && calculatedWidth > containerWidth) {
      const tagElement = tagElements.item(maxIndex - removeCount) as HTMLSpanElement;
      const tagElementMargin = removeCount > 0 ? tagMargin : 0;
      calculatedWidth = calculatedWidth - tagElement.clientWidth - tagElementMargin;
      removeCount++;
      if (!isTruncated) {
        isTruncated = true;
      }
    }

    if (removeCount !== 0 || this.displayedTags$.getValue().length !== this.transformedTagNames.length) {
      this.updateDisplayedTags(slice(this.transformedTagNames, 0, this.transformedTagNames.length - removeCount));
    }

    this.lastContainerSize = containerWidth;
    this.changeDetectorRef.detectChanges();
  }

  private hideMultiLineOverflow(): void {
    const container = this.element.nativeElement as HTMLDivElement;
    const display = this.tagDisplay.nativeElement as HTMLDivElement;

    const lineSize = container.clientWidth;

    const tagElements = display.children;
    const lineLimit =
      tagElements.length > 0 ? Math.floor(container.offsetHeight / (tagElements.item(0) as HTMLSpanElement).offsetHeight) : 0;

    if (lineLimit !== 0) {
      const maxIndex = tagElements.length - 1;
      let lineCount = 1;
      let remainingWidth = lineSize;
      let removeCount = 0;

      for (let i = 0; i < maxIndex; i++) {
        const tagElement = tagElements.item(i) as HTMLSpanElement;
        if (remainingWidth < tagElement.offsetWidth) {
          lineCount += 1;
          remainingWidth = lineSize - tagElement.offsetWidth;
        } else {
          remainingWidth = remainingWidth - tagElement.offsetWidth;
        }

        if (lineCount === lineLimit) {
          removeCount = tagElements.length - i;
          break;
        }
      }

      if (removeCount !== 0 || this.displayedTags$.getValue().length !== this.transformedTagNames.length) {
        this.updateDisplayedTags(slice(this.transformedTagNames, 0, this.transformedTagNames.length - removeCount));
      } else {
        this.updateDisplayedTags(this.transformedTagNames);
      }

      this.lastContainerSize = container.offsetHeight;
    }
    this.changeDetectorRef.markForCheck();
  }

  private updateDisplayedTags(items: Array<string | INimbusTag>): void {
    this.displayedTags$.next(items);
  }
}
